import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiAward, FiPlus, FiSearch, FiEye, FiThumbsUp, FiUsers, FiClock, FiEdit2 } from 'react-icons/fi';
import ContestService from '../services/ContestService';

const Contests = () => {
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');

  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    setLoading(true);
    try {
      const res = await ContestService.getAllContests().catch(() => null);
      const list = res?.contests || res?.data || res || [];
      setContests(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching partner contests:', err);
    } finally {
      setLoading(false);
    }
  };

  const getExpiryInfo = (expiryDate) => {
    if (!expiryDate) return { text: 'NO EXPIRY', isExpired: false, badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
    const exp = new Date(expiryDate);
    const now = new Date();
    const diffMs = exp - now;

    if (diffMs <= 0) {
      return {
        text: 'EXPIRED',
        isExpired: true,
        badgeClass: 'bg-rose-50 text-rose-800 border-rose-200'
      };
    }

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) {
      return {
        text: `LIVE (${diffDays}d left)`,
        isExpired: false,
        badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200'
      };
    }
    return {
      text: `LIVE (${diffHours}h left)`,
      isExpired: false,
      badgeClass: 'bg-amber-50 text-amber-800 border-amber-200'
    };
  };

  const filtered = contests.filter(c => {
    const matchesSearch = (c.title || '').toLowerCase().includes(search.toLowerCase());
    const expiryInfo = getExpiryInfo(c.expiryDate);
    if (filterStatus === 'LIVE') return matchesSearch && !expiryInfo.isExpired;
    if (filterStatus === 'EXPIRED') return matchesSearch && expiryInfo.isExpired;
    return matchesSearch;
  });

  return (
    <div className="space-y-6 text-left pb-16">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
            <FiAward className="text-purple-700" /> Contests & Challenges
          </h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Host competitions, attached event integration, real-time votes & countdowns</p>
        </div>
        <Link
          to="/app/contests/create"
          className="px-4 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-2 self-start md:self-auto"
        >
          <FiPlus className="w-4 h-4" /> + Create Contest
        </Link>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white border border-slate-200 p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm">
        <div className="relative w-full md:w-80">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contests by title..."
            className="w-full bg-white border border-slate-300 rounded-xl py-2 pl-9 pr-4 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 font-medium"
          />
          <FiSearch className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          {['ALL', 'LIVE', 'EXPIRED'].map((st) => (
            <button
              key={st}
              onClick={() => setFilterStatus(st)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                filterStatus === st ? 'bg-purple-700 text-white shadow-xs' : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
              }`}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* Contests Grid */}
      {loading ? (
        <div className="p-12 text-center text-slate-500 text-xs font-bold">Loading partner contests...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => {
            const expiryInfo = getExpiryInfo(c.expiryDate);
            const posterImg = c.posterUrl || c.bannerUrl || c.poster?.url || c.banner?.url || 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=600&q=80';

            return (
              <div key={c._id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:border-purple-300 transition-all flex flex-col justify-between group">
                <div className="space-y-3">
                  {/* Contest Image Banner */}
                  <div className="relative h-44 w-full bg-slate-100 overflow-hidden">
                    <img
                      src={posterImg}
                      alt={c.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=600&q=80'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />

                    <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                      <span className="px-2.5 py-1 rounded-full bg-white/90 text-purple-900 border border-purple-200 text-[10px] font-extrabold uppercase tracking-wider shadow-xs">
                        {c.category || c.categorySlug || c.type || 'Photo Contest'}
                      </span>
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold border flex items-center gap-1 shadow-xs ${expiryInfo.badgeClass}`}>
                        <FiClock className="w-3 h-3" />
                        {expiryInfo.text}
                      </span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="px-5 space-y-3">
                    <h3 className="text-base font-bold text-slate-900 leading-tight line-clamp-2">{c.title}</h3>

                    {c.prizeonewords && (
                      <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-[11px] text-slate-700">
                        <span className="text-slate-500 block text-[9px] uppercase font-bold">Prize</span>
                        <strong className="text-amber-700 font-bold">{c.prizeonewords}</strong>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
                      <div className="flex items-center gap-1.5 text-slate-700 bg-slate-50 border border-slate-200 p-2 rounded-xl">
                        <FiUsers className="text-purple-700 shrink-0" />
                        <span><strong className="text-slate-900">{c.participantsCount || 0}</strong> Entries</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-700 bg-slate-50 border border-slate-200 p-2 rounded-xl">
                        <FiThumbsUp className="text-amber-600 shrink-0" />
                        <span><strong className="text-slate-900">{c.totalVotes || 0}</strong> Votes</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Actions Footer */}
                <div className="p-4 mt-4 border-t border-slate-100 flex items-center justify-between gap-2 text-xs">
                  <Link
                    to={`/app/contests/${c.slug || c._id}/submissions`}
                    className="flex-1 px-3 py-2 rounded-xl bg-purple-50 text-purple-900 border border-purple-200 font-bold hover:bg-purple-100 transition-all flex items-center justify-center gap-1.5 shadow-xs"
                  >
                    <FiEye className="w-3.5 h-3.5" />
                    <span>View Submissions</span>
                  </Link>

                  <Link
                    to={`/app/contests/${c._id}/edit`}
                    className="px-3.5 py-2 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:text-slate-900 hover:bg-slate-200 transition-colors flex items-center gap-1 font-bold shadow-xs"
                    title="Edit Contest"
                  >
                    <FiEdit2 className="w-3.5 h-3.5" />
                    <span>Edit</span>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Contests;
